









































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
import { ActionMethod } from "vuex";

import moment from "moment";
import { Configuration } from "@/services/Configuration";
import { sortReferrals } from "@/store/espoed/index";
import ReferralListComponent from "@/components/ReferralList.vue";
import DefaultPatientSearchDialog from "@/components/espoed/DefaultPatientSearchDialog.vue";
import { PagingOptions, GenericReferral, SignalREvents } from "@/types/types";
import { ReferralLoadOptions, ReferralModel } from "@/types/aznconnect_api";
import SignalRHub from "@/services/SignalRHub";

import MainToolbar from "@/views/espoed/MainToolbar.vue";
import { ApplicationConfiguration, UserPermissions } from "@/types/store";
import SelfReferrerDialog from "@/components/selfreferrer/SelfReferrerDialog.vue";

const DEFAULT_CLUSTER: string = "-";
const DEFAULT_DEPARTMENT: string = "-";
const DEFAULT_TYPE: string = "-";
const DEFAULT_PRIORITY: string = "-";

@Component({
  components: {
    "main-toolbar": MainToolbar,
    "referral-list": ReferralListComponent,
    "default-patient-search-dialog": DefaultPatientSearchDialog,
    "self-referrer-dialog": SelfReferrerDialog,
  },
})
export default class ListView extends Vue {
  @Prop({ default: DEFAULT_CLUSTER })
  cluster!: string;

  @Prop({ default: DEFAULT_DEPARTMENT })
  department!: string;

  @Prop({ default: DEFAULT_TYPE })
  type!: string;

  @Prop({ default: DEFAULT_PRIORITY })
  priority!: string;

  @Prop({ default: null })
  signalRHub!: SignalRHub;

  private resetAndLoad() {
    this.hasMoreData = true;
    this.loadReferrals(true);
  }

  @Watch("cluster") onClusterChanged(val: string, oldVal: string) {
    this.resetAndLoad();
  }

  @Watch("department") onDepartementChanged(val: string, oldVal: string) {
    this.resetAndLoad();
  }

  @Watch("type") onTypeChanged(val: string, oldVal: string) {
    this.resetAndLoad();
  }

  @Watch("priority") onPriorityChanged(val: string, oldVal: string) {
    this.resetAndLoad();
  }

  private currentAudio?: HTMLAudioElement;

  // Starts or stops the repeating notification sound interval based on the current state.
  private updateNotificationSoundRepeatInterval() {
    const shouldRepeat =
      this.playSoundNotificationReferrals &&
      this.applicationConfiguration.inboxNotificationsRepeatUntilReadAll &&
      this.unopenedCount > 0;

    if (shouldRepeat && !this.notificationInterval) {
      this.notificationInterval = window.setInterval(() => {
        if (this.playSoundNotificationReferrals && this.unopenedCount > 0) {
          this.playNotificationSound(1);
        } else {
          this.updateNotificationSoundRepeatInterval();
        }
      }, 2000);
    } else if (!shouldRepeat && this.notificationInterval) {
      window.clearInterval(this.notificationInterval);
      this.notificationInterval = undefined;
    }
  }

  private playNotificationSound(repeatCount: number = 3) {
    // Stop any currently playing sound
    if (this.currentAudio) {
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null as any;
      this.currentAudio.pause();
      this.currentAudio = undefined;
    }

    const audio = new Audio(require("@/assets/dingdong2.wav"));
    audio.volume = 1.0;
    this.currentAudio = audio;

    let playCount = 0;

    audio.onended = () => {
      playCount++;
      if (playCount < repeatCount) {
        audio.play().catch(() => {
          this.currentAudio = undefined;
        });
      } else {
        this.currentAudio = undefined;
      }
    };

    audio.onerror = () => {
      this.currentAudio = undefined;
    };

    audio.play().catch(() => {
      this.currentAudio = undefined;
    });
  }

  @Watch("playSoundNotificationReferrals")
  onPlaySoundNotificationReferralsChanged(newVal: boolean) {
    if (newVal && this.unopenedCount > 0) {
      this.playNotificationSound(1);
    }
    this.updateNotificationSoundRepeatInterval();
  }

  @Watch("unopenedCount")
  onUnopenedCountChanged() {
    this.updateNotificationSoundRepeatInterval();
  }

  @Getter("referrals", { namespace: "espoed" })
  referrals!: Array<GenericReferral>;

  @Getter("applicationConfiguration")
  applicationConfiguration!: ApplicationConfiguration;

  @Getter("userPermissions")
  userPermissions!: UserPermissions;

  @Getter("playSoundNotificationReferrals")
  playSoundNotificationReferrals!: boolean;

  showList: boolean = true;
  paging: PagingOptions = new PagingOptions(50, undefined);
  loading: boolean = false;
  hasMoreData: boolean = true;
  cleanInterval?: number = undefined;
  notificationInterval?: number = undefined;
  currentTime: Date = new Date();
  groupingInterval?: number = undefined;

  isEditingPatientNumber: boolean = false;
  patientNumberReferralReference?: string = "";
  addNewSelfReferrer: boolean = false;

  @Action("loadReferrals", { namespace: "espoed" })
  loadReferralsAction!: ActionMethod;

  @Action("cleanReferrals", { namespace: "espoed" })
  cleanReferrals!: ActionMethod;

  @Action("resolveReferral", { namespace: "espoed" })
  resolveReferralAction!: ActionMethod;

  @Action("removeReferral", { namespace: "espoed" })
  removeReferral!: ActionMethod;

  @Action("addReferral", { namespace: "espoed" })
  addReferral!: ActionMethod;

  @Action("toggleNewReferralSound")
  toggleNewReferralSound!: ActionMethod;

  @Action("updateReferralOpenedState", { namespace: "espoed" })
  updateReferralOpenedState!: ActionMethod;

  @Action("updateEtaTime", { namespace: "espoed" })
  updateEtaTime!: ActionMethod;

  get configuration() {
    return Configuration;
  }

  get unopenedCount(): number {
    return this.referrals.filter(r => !r.isOpened && r.state !== "RESOLVED")
      .length;
  }

  get futureReferrals(): GenericReferral[] {
    const threshold = moment(this.currentTime).add(
      Configuration.pastReferralThresholdMinutes,
      "minutes",
    );
    const filtered = this.referrals.filter(r => {
      const eta = moment(
        `${r.etaDate || ""} ${r.etaTime || ""}`,
        "DD-MM-YYYY HH:mm:ss",
        true,
      );
      // No valid ETA → treat as upcoming (no date = not yet known)
      return !eta.isValid() || eta.isAfter(threshold);
    });
    return sortReferrals(filtered);
  }

  get pastReferrals(): GenericReferral[] {
    const threshold = moment(this.currentTime).add(
      Configuration.pastReferralThresholdMinutes,
      "minutes",
    );
    const filtered = this.referrals.filter(r => {
      const eta = moment(
        `${r.etaDate || ""} ${r.etaTime || ""}`,
        "DD-MM-YYYY HH:mm:ss",
        true,
      );
      // Only referrals with a valid ETA at or before now + pastReferralThresholdMinutes
      return eta.isValid() && !eta.isAfter(threshold);
    });
    return sortReferrals(filtered);
  }

  openLinkPatient(referral: GenericReferral) {
    this.patientNumberReferralReference = referral.reference;
    this.isEditingPatientNumber = true;
  }

  clickedReferralResolve(referral: GenericReferral) {
    this.resolveReferralAction(referral.reference);
  }

  openDetails(referral: GenericReferral) {
    this.$router.push({
      name: "referral-list-detail",
      params: { referralId: referral.reference || "" },
    });
  }

  clickedAmbuFeedback(referral: GenericReferral) {
    this.$router.push({
      name: "referral-list-detail",
      params: { referralId: referral.reference || "" },
    });
  }

  openedDetails() {
    // This handler makes sure the list is hidden when details are opened
    this.showList = false;
  }

  closeDetails() {
    this.showList = true;
    this.$router.go(-1);
  }

  closedDetails() {
    // This handler makes sure the list is displayed when using the browsers 'back' button
    this.showList = true;
  }

  loadReferrals(newRequest: boolean): boolean {
    const vm = this;

    if (newRequest) {
      this.paging.datetime = undefined;
      this.hasMoreData = true;
    }

    // Stop if we know there's no more data
    if (!this.hasMoreData && !newRequest) {
      return false;
    }

    this.loading = true;

    let pagingOptions = new PagingOptions(
      this.paging.pageSize,
      this.paging.datetime,
    );

    const loadOptions: ReferralLoadOptions = new ReferralLoadOptions(
      this.cluster,
      this.department,
      this.type,
      this.priority,
      pagingOptions,
    );

    this.loadReferralsAction(loadOptions)
      .then(response => {
        const newlyLoaded =
          response && response.newlyLoaded ? response.newlyLoaded : 0;

        // If no new items were added, stop loading on next scroll
        if (newlyLoaded === 0) {
          this.hasMoreData = false;
          return;
        }

        if (this.referrals) {
          const lastElement = this.referrals[this.referrals.length - 1];
          if (lastElement) {
            const etaDateTime = `${lastElement.etaDate} ${lastElement.etaTime}`;
            const etaTime = moment(etaDateTime, "DD-MM-YYYY HH:mm:ss", true);

            if (etaTime.isValid()) {
              this.paging.datetime = etaTime.format();
            } else {
              const messageDateTime = moment(lastElement.messageDateTime);
              if (messageDateTime.isValid()) {
                this.paging.datetime = messageDateTime.format();
              }
            }
          }
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error("Error loading referrals: " + error);
      })
      .finally(() => {
        vm.loading = false;
      });

    return false;
  }

  toggleSound() {
    this.toggleNewReferralSound(!this.playSoundNotificationReferrals);
  }

  beforeMount(): void {
    this.loadReferrals(true);
  }

  created(): void {
    if (this.signalRHub != null) {
      this.signalRHub.registerOnConnectedHandler(this.reconnectedHandler);
      this.signalRHub.hubProxy.on(
        SignalREvents.AddReferral,
        this.newReferralHandler,
      );
      this.signalRHub.hubProxy.on(
        SignalREvents.ManualReferralClose,
        this.manualReferralCloseHandler,
      );
      this.signalRHub.hubProxy.on(
        SignalREvents.ReferralOpened,
        this.referralOpenedHandler,
      );
      this.signalRHub.hubProxy.on(
        SignalREvents.UpdateEtaTime,
        this.etaTimeUpdatedHandler,
      );
    }
  }

  mounted() {
    this.groupingInterval = window.setInterval(() => {
      this.currentTime = new Date();
    }, 30000);

    if (this.applicationConfiguration.autoHideNonClosedReferrals) {
      const intervalTime: number = 15 * 60 * 1000;
      const vm = this;
      this.cleanInterval = window.setInterval(function() {
        vm.cleanReferrals();
      }, intervalTime);
    }
  }

  beforeDestroy(): void {
    if (this.groupingInterval) {
      window.clearInterval(this.groupingInterval);
    }

    if (this.cleanInterval) {
      window.clearInterval(this.cleanInterval);
    }

    if (this.notificationInterval) {
      window.clearInterval(this.notificationInterval);
      this.notificationInterval = undefined;
    }

    if (this.signalRHub != null) {
      this.signalRHub.unregisterOnConnectedHandler(this.reconnectedHandler);
      this.signalRHub.hubProxy.off(
        SignalREvents.AddReferral,
        this.newReferralHandler,
      );
      this.signalRHub.hubProxy.off(
        SignalREvents.ManualReferralClose,
        this.manualReferralCloseHandler,
      );
      this.signalRHub.hubProxy.off(
        SignalREvents.ReferralOpened,
        this.referralOpenedHandler,
      );
      this.signalRHub.hubProxy.off(
        SignalREvents.UpdateEtaTime,
        this.etaTimeUpdatedHandler,
      );
    }
  }

  private hasSignalrConnectedOnce = false;
  reconnectedHandler() {
    // Only act on reconnects, since beforeMount takes care of the initial load
    if (this.hasSignalrConnectedOnce) {
      this.loadReferrals(true);
    } else {
      this.hasSignalrConnectedOnce = true;
    }
  }

  async newReferralHandler(referral: ReferralModel) {
    // Always remove the referral and only add it when dept and cluster match (to support removal of referral upon change of destination)
    const referralRemoved = await this.removeReferral(referral.Reference);

    let referralMatchesFilterSettings =
      (this.cluster == DEFAULT_CLUSTER ||
        referral.ClusterCode == this.cluster) &&
      (this.department == DEFAULT_DEPARTMENT ||
        referral.DepartmentCode == this.department) &&
      (this.type == DEFAULT_TYPE || referral.Type == this.type) &&
      (this.priority == DEFAULT_PRIORITY ||
        referral.UrgencyCode == this.priority);

    if (referral.State != "RESOLVED" && referralMatchesFilterSettings) {
      if (
        !referralRemoved &&
        this.playSoundNotificationReferrals &&
        !this.notificationInterval
      ) {
        const repeatCount = this.applicationConfiguration
          .inboxNotificationsRepeatUntilReadAll
          ? 1
          : 3;
        this.playNotificationSound(repeatCount);
      }

      this.addReferral(referral);
    }
  }

  manualReferralCloseHandler(fieldEncounterId: string) {
    this.removeReferral(fieldEncounterId);
  }

  referralOpenedHandler(fieldEncounterId: string) {
    this.updateReferralOpenedState(fieldEncounterId);
  }

  etaTimeUpdatedHandler(referenceId: string, etaDate: string, etaTime: string) {
    this.updateEtaTime({ referenceId, etaDate, etaTime });
  }
}
